import { useMutation, useQueryClient } from 'react-query';

import {
  inventoryExitedQueryKey,
  inventoryItemQueryKey,
  inventoryQueryKey,
} from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { inventoryApi } from 'src/Services/API';
import {
  CreateInventoryItemDto,
  ExitInventoryItemDto,
  InventoryCreatedDto,
  InventoryInvoiceDto,
  InventoryItemDto,
  InventoryPdfDto,
  InventorySettingsDto,
  UpdateInventoryItemDto,
  UpdateInventorySettingsDto,
} from 'src/Services/API/generated';

/** The inventory also feeds the "Lié à" panels (rooms, documents) and the actions. */
const useInvalidateInventory = () => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();
  return (itemId?: string) => {
    queryClient.invalidateQueries(inventoryQueryKey(assetId));
    queryClient.invalidateQueries(inventoryExitedQueryKey(assetId));
    queryClient.invalidateQueries(['linked-entities', assetId]);
    if (itemId) queryClient.invalidateQueries(inventoryItemQueryKey(assetId, itemId));
  };
};

export const useCreateInventoryItem = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  const queryClient = useQueryClient();
  return useMutation<InventoryCreatedDto, unknown, CreateInventoryItemDto>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerCreate(assetId, input)).data;
    },
    {
      onSuccess: item => {
        invalidate(item.id);
        if (item.createdTaskId) queryClient.invalidateQueries(['tasks', assetId]);
      },
    }
  );
};

export const useUpdateInventoryItem = (itemId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  return useMutation<InventoryItemDto, unknown, UpdateInventoryItemDto>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerUpdate(assetId, itemId, input)).data;
    },
    { onSuccess: () => invalidate(itemId) }
  );
};

export const useExitInventoryItem = (itemId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  return useMutation<InventoryItemDto, unknown, ExitInventoryItemDto>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerExit(assetId, itemId, input)).data;
    },
    { onSuccess: () => invalidate(itemId) }
  );
};

export const usePutBackInventoryItem = (itemId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  return useMutation<InventoryItemDto, unknown, void>(
    async () => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerPutBack(assetId, itemId)).data;
    },
    { onSuccess: () => invalidate(itemId) }
  );
};

export const useDeleteInventoryItem = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  return useMutation<void, unknown, string>(
    async itemId => {
      if (!assetId) throw new Error('No assetId provided');
      await inventoryApi.inventoryControllerRemove(assetId, itemId);
    },
    { onSuccess: (_, itemId) => invalidate(itemId) }
  );
};

export const useRestoreInventoryItem = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  return useMutation<InventoryItemDto, unknown, string>(
    async itemId => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerRestore(assetId, itemId)).data;
    },
    { onSuccess: item => invalidate(item.id) }
  );
};

export const useUploadInventoryInvoice = () => {
  const assetId = useCurrentAssetId();
  return useMutation<InventoryInvoiceDto, unknown, File>(async file => {
    if (!assetId) throw new Error('No assetId provided');
    return (await inventoryApi.inventoryControllerUploadInvoice(assetId, file)).data;
  });
};

export const useUpdateInventorySettings = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateInventory();
  return useMutation<InventorySettingsDto, unknown, UpdateInventorySettingsDto>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return (await inventoryApi.inventoryControllerUpdateSettings(assetId, input)).data;
    },
    { onSuccess: () => invalidate() }
  );
};

/** Opens the tab inside the click (popup blockers), then points it at the signed PDF. */
export const openInventoryPdf = async (
  request: () => Promise<InventoryPdfDto>
): Promise<InventoryPdfDto> => {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  try {
    const pdf = await request();
    if (tab) tab.location.href = pdf.url;
    else window.location.assign(pdf.url);
    return pdf;
  } catch (error) {
    tab?.close();
    throw error;
  }
};
